export const removeRepeatedDecimalPoints = (
  value: string | undefined | null
): string => {
  if (!value) return '';

  // Note: Workaround for Browsers that lack support for Lookbehind (Safari 16.3 and below)
  // value.replace(/(?<=\d*\.\d*)\./g, '')
  const firstDotIndex = value.indexOf('.');

  return value
    .split('')
    .filter(
      (char, index) => char !== '.' || (char === '.' && index === firstDotIndex)
    )
    .join('');
};

export const processPositiveNumberInput = (
  value: string | undefined | null
): string => {
  if (!value) return '';
  if (value === '.') return '0.';
  return removeRepeatedDecimalPoints(value.replace(/[^\d.]/g, ''));
};

export const processRealNumberInput = (
  value: string | undefined | null
): string => {
  if (!value) return '';
  if (value === '.') return '0.';
  return removeRepeatedDecimalPoints(value.replace(/[^\-\d.]/g, ''));
};

/**
 * Strips the number of decimal places exceeding the given number.
 * Useful in onChange handler to restrict users from entering more decimals than allowed.
 */
export const stripFractionsExceedingDecimals = (
  value: string | undefined | null,
  decimals: number | undefined | null
): string => {
  if (!value) return '';
  if (decimals === null || typeof decimals === 'undefined') return value;

  if (decimals === 0) return value.replace(/^(-?\d*)\.(.)*/g, '$1');

  return value
    .replace(/[^\d.-]/g, '')
    .replace(new RegExp(`^(-?\\d+\\.?\\d{0,${decimals}})(\\d*)$`), '$1');
};

/**
 * Returns the number of decimal places in a number
 */
export const getDecimalCount = (value: number) => {
  if (typeof value !== 'number' || !isFinite(value)) return 0;

  if (Math.floor(value.valueOf()) === value.valueOf()) return 0;

  const str = value.toString();
  if (str.indexOf('.') !== -1 && str.indexOf('-') !== -1) {
    return Number(str.split('-')[1]) || 0;
  } else if (str.indexOf('.') !== -1) {
    return str.split('.')[1].length || 0;
  }
  return Number(str.split('-')[1]) || 0;
};

/**
 * Rounds number to the nearest value with a given precision
 */
export const roundNumber = (value: number, maxPrecision = 2) => {
  const targetPrecision = Math.min(getDecimalCount(value), maxPrecision);
  const factor = Math.pow(10, targetPrecision);

  return Math.round(value * factor) / factor;
};

export const getSignificantPrecision = (value: number) => {
  if (value < 0.001) return 5;
  if (value < 0.01) return 4;
  if (value < 0.1) return 3;

  return 2;
};

/**
 * Rounds number to the nearest value with up to a given max precision
 * based on the number's significance.
 * Minimum precision is 2.
 */
export const roundNumberToSignificant = (value: number, maxPrecision = 2) => {
  return roundNumber(
    value,
    Math.min(getSignificantPrecision(value), maxPrecision)
  );
};

export const parseNumberSafe = (value: string, maxPrecision = 2) => {
  if (!value) return 0;
  const parsedNumber = parseFloat(value);
  return isFinite(parsedNumber) ? roundNumber(parsedNumber, maxPrecision) : 0;
};

export const isValidNumber = (value: string) => {
  if (!value) return true;
  return isFinite(parseFloat(value)) && /^\-?\d+(\.\d+)?$/.test(value);
};
